import { BookingOrchestrator } from '../booking-orchestrator';
import {
  ActionContextType,
  BookingContext,
  BookingPosition,
  BookingServices,
  StockSourceType,
  SystemContext,
} from '../types';
import { FromBufferstockBuilder } from './from-bufferstock-builder';
import { FromCarBuilder } from './from-car-builder';
import { FromMachineBuilder } from './from-machine-builder';
import { BookingTransaction } from '../transaction/booking-transaction';

/**
 * Erste Builder-Stage. Einstieg in die Buchungs-API über
 * `BookingOrchestratorService.bookPositions(...)`.
 *
 * Pflicht: genau eine `fromX()`-Methode aufrufen, um die Quelle zu wählen.
 * Die Quelle bestimmt den nachfolgenden Source-Builder, der wiederum nur die
 * fachlich sinnvollen Ziel-Optionen anbietet (Compile-Time-Sicherheit).
 */
export class SourceBuilder {
  /**
   * @param orchestrator Kernel, das die einzelnen Position-Buchungen ausführt
   * @param systemContext System-weite Konfiguration aus den Funktionsschaltern
   * @param bookingContext Beim Einstieg noch leerer Kontext, der stufenweise befüllt wird
   * @param services Von der App injizierte Model- und Warehouse-Services
   * @param positions Liste der zu buchenden Positionen (bereits beim Einstieg
   *   gesetzt: Pflichtfeld jeder Buchung)
   */
  constructor(
    private readonly orchestrator: BookingOrchestrator,
    private readonly systemContext: SystemContext,
    private readonly bookingContext: Partial<BookingContext>,
    private readonly services: BookingServices,
    private readonly positions: BookingPosition[],
  ) {}

  /**
   * Anmerkung des Users (z.B. Begründung einer Korrektur-Buchung).
   *
   * @param description Freitext-Anmerkung zur Buchung
   * @returns Dieser Builder für Method-Chaining
   */
  public withDescription(description: string): this {
    this.bookingContext.description = description;
    return this;
  }

  /**
   * Setzt die Beleg-/Gruppierungs-Referenz der Buchung (`booking_reference`).
   * Ohne Aufruf vergibt der `BookingRequest` eine Transfer-Referenz. Order-Flows
   * reichen hier `BookingReference.forOrder(...)` durch.
   *
   * @param reference Referenz der Buchung (z.B. `Order <Auftragsnummer>`)
   * @returns Dieser Builder für Method-Chaining
   */
  public withReference(reference: string): this {
    this.bookingContext.bookingReference = reference;
    return this;
  }

  /**
   * Aktivität, in der sich der User gerade befindet (Service vs. Befüllen).
   * Beeinflusst Pre-Conditions und Default-Quellen-Logik.
   *
   * @param actionContext Aktueller Aktivitätskontext des Users
   * @returns Dieser Builder für Method-Chaining
   */
  public withActionContext(actionContext: ActionContextType): this {
    this.bookingContext.actionContext = actionContext;
    return this;
  }

  /**
   * Bindet die Buchung an eine bestehende Transaktion. Die Buchung wird normal
   * ausgeführt, aber zusätzlich in der Transaktion vermerkt, damit sie später
   * per Commit/Rollback abgeschlossen oder zurückgebucht werden kann.
   *
   * @param transaction Transaktion, an die die Buchung gebunden wird
   * @returns Dieser Builder für Method-Chaining
   */
  public withTransaction(transaction: BookingTransaction): this {
    this.bookingContext.transactionId = transaction.id;
    return this;
  }

  /**
   * Setzt einen Bufferstock als Quelle der Buchung.
   *
   * @param id ID des Quell-Bufferstocks
   * @returns Source-Builder-Stage mit den vom Bufferstock aus möglichen Zielen
   */
  public fromBufferstock(id: number): FromBufferstockBuilder {
    return new FromBufferstockBuilder(
      this.orchestrator,
      this.systemContext,
      {
        ...this.bookingContext,
        sourceType: StockSourceType.BUFFERSTOCK,
        sourceId: id,
      },
      this.services,
      this.positions,
    );
  }

  /**
   * Setzt ein KFZ als Quelle der Buchung.
   *
   * @param id ID des Quell-KFZ
   * @returns Source-Builder-Stage mit den vom KFZ aus möglichen Zielen
   */
  public fromCar(id: number): FromCarBuilder {
    return new FromCarBuilder(
      this.orchestrator,
      this.systemContext,
      { ...this.bookingContext, sourceType: StockSourceType.CAR, sourceId: id },
      this.services,
      this.positions,
    );
  }

  /**
   * Setzt eine Maschine als Quelle der Buchung.
   *
   * @param id ID der Quell-Maschine
   * @returns Source-Builder-Stage mit den von der Maschine aus möglichen Zielen
   */
  public fromMachine(id: number): FromMachineBuilder {
    return new FromMachineBuilder(
      this.orchestrator,
      this.systemContext,
      {
        ...this.bookingContext,
        sourceType: StockSourceType.MACHINE,
        sourceId: id,
      },
      this.services,
      this.positions,
    );
  }
}
